import React, { useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate
} from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { addSkipLink } from "./utils/accessibility";
import { ToastProvider } from "./components/ToastContext";
import { CartProvider } from "./components/TempCartContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ModalProvider } from "./contexts/ModalContext";
import { useAnalytics } from "./hooks/useAnalytics";
import Navbar from "./components/Navbar";
import AgentsModal from "./components/AgentsModal";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import BackToTop from "./components/BackToTop";
import CartIcon from "./components/CartIcon";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import PWAInstallButton from "./components/PWAInstallButton";
import {
  SignedIn,
  SignedOut,
  RedirectToSignIn
} from "@clerk/clerk-react";
import "./index.css";

// Add automatic chunk loading error recovery
window.addEventListener('error', (event) => {
  if (event.message && event.message.includes('Loading chunk')) {
    // Intercept chunk loading errors and retry automatically
    const chunkRetryKey = `chunk-failed-${event.message}`;
    const retryCount = parseInt(localStorage.getItem(chunkRetryKey) || '0');

    if (retryCount < 3) {
      // Retry up to 3 times
      localStorage.setItem(chunkRetryKey, (retryCount + 1).toString());

      // Clear module cache and reload chunk after a delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);

      // Prevent error from bubbling up
      event.preventDefault();
    } else {
      // After 3 retries, clear cache and force hard reload
      localStorage.removeItem(chunkRetryKey);
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => caches.delete(name));
        }).then(() => {
          window.location.reload();
        });
      }
    }
  }
});

// Handle unhandled promise rejections for chunk loading
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && event.reason.message && event.reason.message.includes('Loading chunk')) {
    event.preventDefault();

    const chunkRetryKey = `chunk-promise-failed-${event.reason.message}`;
    const retryCount = parseInt(localStorage.getItem(chunkRetryKey) || '0');

    if (retryCount < 2) {
      localStorage.setItem(chunkRetryKey, (retryCount + 1).toString());

      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  }
});

// Lazy load all pages for better performance
const Home = React.lazy(() => import("./pages/Home"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));

const ProductDetail = React.lazy(() => import("./pages/ProductDetail"));
const CartPage = React.lazy(() => import("./pages/CartPage"));
const OrderConfirmation = React.lazy(() => import("./pages/OrderConfirmation"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));
const AboutUs = React.lazy(() => import("./pages/AboutUs"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const FAQ = React.lazy(() => import("./pages/FAQ"));
const SharedCart = React.lazy(() => import("./pages/SharedCart"));
const Review = React.lazy(() => import("./pages/Review"));
const ReviewList = React.lazy(() => import("./pages/ReviewList"));
// Import AdminSignup component
const AdminSignup = React.lazy(() => import("./features/admin/auth/AdminSignup"));
const { CollectionView } = React.lazy(() => import("./pages/Products"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
  </div>
);

function AppContent() {
  // Initialize analytics tracking
  useAnalytics();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col w-full overflow-x-hidden">
      <div className="w-full">
        <Navbar />
      </div>
      <AgentsModal />
      <BackToTop />
      <CartIcon />
      <PWAInstallPrompt />
      <PWAInstallButton />
      <main id="main-content" className="flex-grow w-full max-w-none" role="main">
        <div className="w-full px-0">
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/product/:id" element={<ProductDetail />} />
              <Route
                path="/collection/:id"
                element={<CollectionView />}
              />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/cart/:id" element={<CartPage />} />
              <Route
                path="/cart/share/:shareId"
                element={<SharedCart />}
              />

              {/* <Route path="/signin" element={<SignIn setToken={setToken} />} */}
              {/* <Route path="/signup" element={<SignUp />} /> */}
              <Route path="/admin/login" element={
                <>
                  <SignedIn>
                    <Navigate to="/admin" replace />
                  </SignedIn>
                  <SignedOut>
                    <RedirectToSignIn redirectUrl="/admin" />
                  </SignedOut>
                </>
              } />
              <Route path="/admin/signup" element={<AdminSignup />} />
              <Route
                path="/admin/*"
                element={
                  <>
                    <SignedIn>
                      <AdminDashboard />
                    </SignedIn>
                    <SignedOut>
                      <RedirectToSignIn redirectUrl="/admin" />
                    </SignedOut>
                  </>
                }
              />
              <Route
                path="/order-confirmation"
                element={<OrderConfirmation />}
              />
              <Route path="/contact" element={<ContactUs />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/review" element={<ReviewList />} />
              <Route path="/review/:resellerId" element={<Review />} />
              {/* Catch-all route for 404 errors */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
      </main>
      <div className="w-full">
        <Footer />
      </div>
    </div>
  );
}

function AppWrapper() {
  // Add skip link on mount
  useEffect(() => {
    addSkipLink();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <ModalProvider>
          <ToastProvider>
            <CartProvider>
              <Router>
                <ScrollToTop />
                <AppContent />
              </Router>
            </CartProvider>
          </ToastProvider>
        </ModalProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <AppWrapper />
  );
}



export default App;
